<script setup lang="ts">
import type { MeasureBound, PieceSection, SectionSegment } from '@/types';
import { getMeasureBoundForIndex, getSectionBounds, getSectionSegments, hexToRgba } from '@/utils';
import { computed, onBeforeUnmount, ref } from 'vue';

const LABEL_INSET_PX = 4;
const LABEL_WIDTH_FALLBACK_PX = 200;

const props = withDefaults(defineProps<{
  sections: PieceSection[];
  measureBounds: MeasureBound[];
  /** Horizontal translation applied to the sheet, in local px. */
  scrollOffset: number;
  /**
   * The CSS transform the parent scales this overlay by, if any. Labels stick
   * to a fixed *screen* inset, so they need these to convert that inset back
   * into the local coordinates the strips are laid out in.
   */
  scale?: number;
  originX?: number;
  mode?: 'single' | 'multi';
  showStrips?: boolean;
  stripAlpha?: number;
  stripBorder?: boolean;
  labelTopPx?: number;
  verticalOffset?: number;
}>(), {
  scale: 1,
  originX: 0,
  mode: 'single',
  showStrips: true,
  stripAlpha: 0.18,
  stripBorder: false,
  labelTopPx: -24,
  verticalOffset: 0,
});

// Label widths, reactive so a change re-renders on its own. Measured by a ResizeObserver
// rather than in the ref callback below: that callback used to read `offsetWidth`, forcing a
// synchronous layout once per label on every frame of playback, against a document holding
// the full-width sheet SVG. An observer's callback runs after layout instead, so the same
// read is clean. `observe` reports the initial size straight away — no measure-on-mount.
const labelWidths = ref(new Map<string, number>());
const labelObservers = new Map<string, { el: HTMLElement; observer: ResizeObserver }>();

onBeforeUnmount(() => labelObservers.forEach(({ observer }) => observer.disconnect()));

const isMultiMode = computed(() => props.mode === 'multi');

const setLabelRef = (sectionId: string, el: unknown) => {
  const existing = labelObservers.get(sectionId);
  // Vue invokes function refs on every patch, not just on attach, so this has to be
  // idempotent: re-observing an element resets its last-reported size and re-fires the
  // callback, which would put per-frame work right back in.
  if (existing?.el === el) return;
  existing?.observer.disconnect();

  // el is null when Vue unmounts a removed section's label — drop the stale width.
  if (!(el instanceof HTMLElement)) {
    labelObservers.delete(sectionId);
    labelWidths.value.delete(sectionId);
    return;
  }

  const observer = new ResizeObserver(() => labelWidths.value.set(sectionId, el.offsetWidth));
  observer.observe(el);
  labelObservers.set(sectionId, { el, observer });
};

const stripStyle = (section: PieceSection): Record<string, string> => {
  const bounds = getSectionBounds(section, props.measureBounds);
  if (!bounds) return {};
  return {
    left: `${bounds.x}px`,
    width: `${bounds.width}px`,
    backgroundColor: hexToRgba(section.color, props.stripAlpha),
    ...(props.stripBorder ? { borderLeft: `3px solid ${section.color}` } : {}),
  };
};

// Labels ride along with their section but stick at the left edge once the
// section's start scrolls past it, and stop before overrunning its end.
const labelStyle = (section: PieceSection): Record<string, string> => {
  const start = getMeasureBoundForIndex(props.measureBounds, section.startMeasureIndex);
  const end = getMeasureBoundForIndex(props.measureBounds, section.endMeasureIndex);
  if (!start || !end) return {};

  const stickyMinLeft = props.originX + (LABEL_INSET_PX - props.originX) / props.scale;
  const labelWidth = labelWidths.value.get(section.id) ?? LABEL_WIDTH_FALLBACK_PX;
  const naturalLeft = start.x + props.scrollOffset + LABEL_INSET_PX;
  const maxLeft = end.x + end.width + props.scrollOffset - labelWidth - LABEL_INSET_PX;

  return {
    left: `${Math.min(Math.max(stickyMinLeft, naturalLeft), maxLeft)}px`,
    top: `${props.labelTopPx}px`,
    color: section.color,
  };
};

const multiStripStyle = (segment: SectionSegment, section: PieceSection): Record<string, string> => ({
  left: `${segment.x}px`,
  top: `${segment.y + props.verticalOffset}px`,
  width: `${segment.width}px`,
  height: `${segment.height}px`,
  backgroundColor: hexToRgba(section.color, props.stripAlpha),
  ...(props.stripBorder ? { borderLeft: `3px solid ${section.color}` } : {}),
});

const multiLabelStyle = (segment: SectionSegment, section: PieceSection): Record<string, string> => ({
  left: `${segment.x + LABEL_INSET_PX}px`,
  top: `${segment.y + props.verticalOffset + props.labelTopPx}px`,
  color: section.color,
  maxWidth: `${Math.max(segment.width - LABEL_INSET_PX * 2, 80)}px`,
});
</script>

<template>
  <template v-if="props.sections.length && props.measureBounds.length">
    <template v-if="!isMultiMode">
      <!-- Colour strips travel with the sheet. -->
      <div
        v-if="props.showStrips"
        class="absolute inset-0 pointer-events-none"
        :style="{ transform: `translateX(${props.scrollOffset}px)` }"
      >
        <div
          v-for="section in props.sections"
          :key="`strip-${section.id}`"
          class="absolute top-0 bottom-0"
          :style="stripStyle(section)"
        />
      </div>

      <!-- Labels are positioned absolutely instead, so they can stick. -->
      <div class="absolute inset-0 pointer-events-none">
        <div
          v-for="section in props.sections"
          :key="`label-${section.id}`"
          :ref="(el) => setLabelRef(section.id, el)"
          class="absolute px-2 py-1 rounded text-xs font-semibold shadow-lg bg-background/80 backdrop-blur-sm whitespace-nowrap"
          :style="labelStyle(section)"
        >
          {{ section.name }}
        </div>
      </div>
    </template>

    <template v-else>
      <div
        v-if="props.showStrips"
        class="absolute inset-0 pointer-events-none"
      >
        <template
          v-for="section in props.sections"
          :key="`strip-${section.id}`"
        >
          <div
            v-for="(segment, index) in getSectionSegments(section, props.measureBounds)"
            :key="`segment-${section.id}-${index}`"
            class="absolute rounded-sm"
            :style="multiStripStyle(segment, section)"
          />
        </template>
      </div>

      <div class="absolute inset-0 pointer-events-none">
        <template
          v-for="section in props.sections"
          :key="`label-${section.id}`"
        >
          <template
            v-for="(segment, index) in getSectionSegments(section, props.measureBounds)"
            :key="`label-${section.id}-${index}`"
          >
            <div
              v-if="segment.isFirst"
              :ref="(el) => setLabelRef(section.id, el)"
              class="absolute px-2 py-1 rounded text-xs font-semibold shadow-lg bg-background/80 backdrop-blur-sm whitespace-nowrap overflow-hidden"
              :style="multiLabelStyle(segment, section)"
            >
              {{ section.name }}
            </div>
          </template>
        </template>
      </div>
    </template>
  </template>
</template>
